import { Fragment, type ReactNode } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Notepad — minimal, dependency-free, XSS-safe Markdown preview.
//
// Renders a professional read view of the note's plain-text body. It parses a
// pragmatic subset (headings, bold/italic, inline code, links, blockquotes,
// bullet/ordered/task lists, fenced code, horizontal rules) into REACT ELEMENTS
// — never `dangerouslySetInnerHTML` — so every value is auto-escaped by React
// and untrusted note content can never inject markup or scripts.
// ─────────────────────────────────────────────────────────────────────────────

/** Creates a per-render sequential key generator (keeps render pure — no shared
 * module-level mutable counter). */
function createKeyGen(): () => string {
  let seq = 0;
  return () => `md-${seq++}`;
}

/** Parse inline spans: bold, italic, inline code, and links. */
function renderInline(text: string, nextKey: () => string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Order matters: code first (so ** inside code is literal), then links, then emphasis.
  const pattern = /(`[^`]+`)|(\[[^\]]+\]\((?:https?:\/\/|\/)[^)\s]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) nodes.push(<Fragment key={nextKey()}>{text.slice(last, m.index)}</Fragment>);
    const tok = m[0];
    if (tok.startsWith('`')) {
      nodes.push(
        <code key={nextKey()} className="rounded bg-surface-variant px-1.5 py-0.5 font-mono text-[0.85em] text-on-surface">
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith('[')) {
      const mm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (mm) {
        nodes.push(
          <a key={nextKey()} href={mm[2]} target="_blank" rel="noreferrer noopener" className="text-primary underline underline-offset-2 hover:opacity-80">
            {mm[1]}
          </a>,
        );
      }
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={nextKey()} className="font-semibold text-on-surface">{tok.slice(2, -2)}</strong>);
    } else {
      nodes.push(<em key={nextKey()} className="italic">{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(<Fragment key={nextKey()}>{text.slice(last)}</Fragment>);
  return nodes;
}

interface ListItem { text: string; task?: 'open' | 'done' }

export function MarkdownPreview({ text }: { text: string }) {
  const nextKey = createKeyGen();
  const lines = (text ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];

  let i = 0;
  const flushList = (ordered: boolean, items: ListItem[]) => {
    if (items.length === 0) return;
    const isTask = items.some((it) => it.task);
    if (isTask) {
      blocks.push(
        <ul key={nextKey()} className="my-2 space-y-1">
          {items.map((it) => (
            <li key={nextKey()} className="flex items-start gap-2 text-body-md text-on-surface">
              <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${it.task === 'done' ? 'border-primary bg-primary text-on-primary' : 'border-outline'}`}>
                {it.task === 'done' ? '✓' : ''}
              </span>
              <span className={it.task === 'done' ? 'text-on-surface-variant line-through' : ''}>{renderInline(it.text, nextKey)}</span>
            </li>
          ))}
        </ul>,
      );
      return;
    }
    const Tag = ordered ? 'ol' : 'ul';
    blocks.push(
      <Tag key={nextKey()} className={`my-2 space-y-1 pl-5 text-body-md text-on-surface ${ordered ? 'list-decimal' : 'list-disc'}`}>
        {items.map((it) => <li key={nextKey()}>{renderInline(it.text, nextKey)}</li>)}
      </Tag>,
    );
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block.
    if (/^```/.test(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { code.push(lines[i]); i++; }
      i++; // consume closing fence
      blocks.push(
        <pre key={nextKey()} className="my-2 overflow-x-auto rounded-card-sm bg-surface-variant p-3 font-mono text-body-sm text-on-surface">
          <code>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // Horizontal rule.
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push(<hr key={nextKey()} className="my-4 border-outline" />);
      i++;
      continue;
    }

    // Headings.
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const cls = level === 1
        ? 'mt-4 mb-2 text-display-sm font-semibold text-on-surface'
        : level === 2
          ? 'mt-3 mb-1.5 text-headline-md font-semibold text-on-surface'
          : 'mt-2 mb-1 text-[15px] font-semibold text-on-surface';
      const Tag = (`h${level}`) as 'h1' | 'h2' | 'h3';
      blocks.push(<Tag key={nextKey()} className={cls}>{renderInline(h[2], nextKey)}</Tag>);
      i++;
      continue;
    }

    // Blockquote (consecutive `>` lines).
    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { quote.push(lines[i].replace(/^>\s?/, '')); i++; }
      blocks.push(
        <blockquote key={nextKey()} className="my-2 border-l-2 border-primary/50 pl-3 text-body-md italic text-on-surface-variant">
          {quote.map((q) => <p key={nextKey()}>{renderInline(q, nextKey)}</p>)}
        </blockquote>,
      );
      continue;
    }

    // Lists (unordered / ordered / task) — collect consecutive list lines.
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items: ListItem[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i]) && /^\s*\d+\.\s+/.test(lines[i]) === ordered) {
        const raw = lines[i].replace(/^\s*([-*]|\d+\.)\s+/, '');
        const task = /^\[ \]\s+/.test(raw) ? 'open' : /^\[x\]\s+/i.test(raw) ? 'done' : undefined;
        items.push({ text: task ? raw.replace(/^\[[ x]\]\s+/i, '') : raw, task });
        i++;
      }
      flushList(ordered, items);
      continue;
    }

    // Blank line → spacing.
    if (line.trim() === '') { i++; continue; }

    // Paragraph (gather consecutive non-special lines).
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,3})\s+/.test(lines[i]) &&
      !/^```/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !/^\s*([-*]|\d+\.)\s+/.test(lines[i]) &&
      !/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={nextKey()} className="my-2 whitespace-pre-wrap text-body-md leading-7 text-on-surface">
        {para.map((p, idx) => (
          <Fragment key={nextKey()}>{idx > 0 && <br />}{renderInline(p, nextKey)}</Fragment>
        ))}
      </p>,
    );
  }

  if (blocks.length === 0) {
    return <p className="text-body-md italic text-on-surface-variant">Nothing to preview yet.</p>;
  }
  return <div className="mx-auto max-w-[720px]">{blocks}</div>;
}
